<template>
	<form @submit.prevent="search">
		<label :for="id" class="sr-only">
			{{ t("placeholder") }}
		</label>

		<div
			class="flex items-center gap-x-2 rounded border border-gray-500 bg-white pl-2 focus-within:outline"
		>
			<input
				:id="id"
				v-model="localValue"
				type="text"
				:placeholder="t('placeholder')"
				class="h-9 flex-1 bg-transparent px-0.5 text-black focus:outline-hidden"
				:aria-label="t('placeholder')"
				@focusin="isFocused = true"
				@focusout="isFocused = false"
			/>

			<button
				v-show="localValue !== ''"
				type="button"
				class="flex size-7 items-center justify-center text-gray-800 hover:text-black focus:text-black"
				:aria-label="tg('filter-clear')"
				:title="tg('filter-clear')"
				@click="clear()"
			>
				<v-icon icon="close" class="text-lg" />
			</button>

			<button
				type="submit"
				class="mr-2 text-lg text-gray-800 duration-100 hover:text-black focus:text-black"
				:class="{'animate-[pulse_500ms_ease-in-out_infinite]': isLoading}"
				:aria-label="tg('accessibility.search')"
				:title="tg('accessibility.search')"
			>
				<v-icon icon="search" />
			</button>
		</div>
	</form>
</template>

<script lang="ts" setup>
import {watchDebounced} from "@vueuse/core";
import {omit} from "es-toolkit";

defineProps<{
	isLoading?: boolean;
}>();

const {t} = useI18n();
const {t: tg} = useI18n({useScope: "global"});

const route = useRoute();
const router = useRouter();

const searchQuery = computed(() => route.query.query?.toString());

const id = useId();
const isFocused = ref(false);

const localValue = ref(searchQuery.value || "");

function search() {
	doSearch(localValue.value);
}

function clear() {
	localValue.value = "";
	doSearch(undefined);
}

function doSearch(searchTerm: string | undefined) {
	const newQuery = searchTerm
		? {
				...route.query,
				query: searchTerm,
			}
		: omit(route.query, ["query"]);

	router.replace({path: route.path, query: newQuery});
}

watch(searchQuery, (newValue) => {
	if (newValue !== localValue.value) {
		localValue.value = newValue || "";
	}
});

watchDebounced(
	localValue,
	(value) => {
		doSearch(value);
	},
	{debounce: 300},
);
</script>

<i18n lang="json">
{
	"et": {
		"placeholder": "Otsi kategooriast"
	},
	"fi": {
		"placeholder": "Rajaa hakusanalla"
	}
}
</i18n>
